import { useMemo } from 'react';
import Box from '@mui/material/Box';
import { Sidebar } from './sidebar';
import { TopBar } from './top-bar';
import { TopNav } from './top-nav';
import { ProductDirectoryPanel } from './product-directory-panel';
import { ProductSwitcherProvider, useProductSwitcher } from './product-switcher-context';
import { containerSx, fluidContainerSx, navHeight } from './constants';

import type { SxProps } from '@mui/material/styles';
import type { ReactNode } from 'react';
import type { AppShellProps } from './types';

export function Shell({
  logo,
  navItems,
  account,
  navVariant = 'sidebar',
  collapsed,
  userMenuItems,
  userMenuGroups: userMenuGroupsFromServer,
  logoutDisabled = false,
  linkComponent,
  currentPath,
  emitDocAttributes = false,
  organizations,
  selectedOrganization,
  onOrganizationChange,
  onOrganizationsMenuOpen,
  onAiAssistantClick,
  navActions,
  product = '',
  secondaryNavItems,
  sidebarStatus,
  fluid = false,
  topOffset = 0,
  children,
}: AppShellProps) {
  let shellContainerSx: SxProps = containerSx;
  if (fluid) {
    shellContainerSx = fluidContainerSx;
  }
  // Only this app's own pages. The account hub is reached through the product switcher now, and
  // the identity header and the log out are the menu's own, so neither is assembled here.
  const userMenuGroups = useMemo(() => {
    if (userMenuGroupsFromServer) {
      return userMenuGroupsFromServer;
    }
    return [userMenuItems ?? []];
  }, [userMenuGroupsFromServer, userMenuItems]);

  let navBody: ReactNode;
  if (navVariant === 'horizontal') {
    navBody = (
      <>
        <TopNav
          navItems={navItems}
          currentPath={currentPath}
          linkComponent={linkComponent}
            emitDocAttributes={emitDocAttributes}
          navActions={navActions}
            fluid={fluid}
        />
        <Box sx={shellContainerSx}>
          <Box component="main" sx={{ minWidth: 0 }}>
            {children}
          </Box>
        </Box>
      </>
    );
  } else {
    navBody = (
      <Box sx={shellContainerSx}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Sidebar
            navItems={navItems}
            currentPath={currentPath}
            linkComponent={linkComponent}
            collapsed={collapsed ?? false}
            emitDocAttributes={emitDocAttributes}
            secondaryNavItems={secondaryNavItems}
            sidebarStatus={sidebarStatus}
            stickyTop={navHeight + topOffset}
          />
          <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
            {children}
          </Box>
        </Box>
      </Box>
    );
  }

  const rootSx: Record<string, unknown> = { minHeight: '100vh', bgcolor: 'background.default' };
  const contentSx: Record<string, unknown> = { pt: `${navHeight + topOffset}px` };

  return (
    <ProductSwitcherProvider
      product={product}
      organizations={organizations}
      selectedOrganization={selectedOrganization}
      onOrganizationChange={onOrganizationChange}
      onOrganizationsMenuOpen={onOrganizationsMenuOpen}
    >
      <Box sx={rootSx}>
        <TopBar
          logo={logo}
          account={account}
          userMenuGroups={userMenuGroups}
          logoutDisabled={logoutDisabled}
          emitDocAttributes={emitDocAttributes}
          onAiAssistantClick={onAiAssistantClick}
          fluid={fluid}
        />
        <ShellContent sx={contentSx}>{navBody}</ShellContent>
        <ProductDirectoryPanel />
      </Box>
    </ProductSwitcherProvider>
  );
}

/**
 * The page content, sealed off while the directory covers it so Tab and the screen reader
 * cannot reach what the viewer can no longer see.
 */
function ShellContent({ sx, children }: { sx: Record<string, unknown>; children: ReactNode }) {
  const switcher = useProductSwitcher();
  const isSealed = switcher !== null && switcher.isPanelOpen;
  return (
    <Box sx={sx} inert={isSealed}>
      {children}
    </Box>
  );
}
